import { isClosedStatus } from '@bbbk/schema';
import type { BookingStatus, Viewing } from '@bbbk/schema';
import { StatusPill } from '../common';
import { nightsBetween, nightsLabel } from '../bookings/domain';
import { VIEWING_STATUS_RECORDS } from '../bookings/constants';
import { shortDate } from '../../lib/dates';

// THE VIEWING IN THE HOUSEHOLD'S HISTORY (book-them-in; Sir, 2026-09-02).
//
// A household that began as somebody coming to look round has that visit in
// its story, and the rail timeline is where the story is read. The viewing
// sits among the stays by its own date, tagged as a viewing with its own
// status, and is NEVER a booking: the head still counts the stays alone
// (viewings-in-the-bookings-list AC2), and the node opens the viewing, not a
// booking.

/** What the rail needs of a stay — the modal's own row type loosens the rest. */
export interface TimelineStay { id: string; checkIn: string }

/** What a stay's node shows — the owner modal's row type satisfies it. */
export interface StayNodeData extends TimelineStay { checkOut: string; status: BookingStatus; total: string | null }

export type TimelineItem<B extends TimelineStay = TimelineStay> = { kind: 'booking'; b: B } | { kind: 'viewing'; v: Viewing };

export function mergeTimeline<B extends TimelineStay>(bookings: B[], viewings: Viewing[]): TimelineItem<B>[] {
  return [...bookings.map((b) => ({ kind: 'booking', b }) as const), ...viewings.map((v) => ({ kind: 'viewing', v }) as const)];
}

/** The date the rail sorts and bands by — a stay's check-in, a viewing's day. */
export function timelineDate(i: TimelineItem<TimelineStay>): string {
  return i.kind === 'booking' ? i.b.checkIn : i.v.viewingOn;
}

export function timelineKey(i: TimelineItem<TimelineStay>): string {
  return i.kind === 'booking' ? i.b.id : 'v' + i.v.id;
}

/**
 * A stay's node on the rail — dates and status on the first line, the money and
 * the nights beneath. Lifted from the owner modal so the viewing modal's rail
 * draws the same node (Sir, 2026-09-02: a booking one press from its viewing).
 */
export function BookingNode({ b, on = false, onOpen }: { b: StayNodeData; on?: boolean; onOpen?: (id: string) => void }) {
  const inner = (
    <>
      <div className="spine"><div className="dot" /></div>
      <div className="lab">
        <div className="txt">
          {/* THE STATUS IS A GLYPH AT THE RIGHT END OF THE DATE'S LINE (Freddi,
              2026-10-06). It sat on a line of its own above the dates — moved
              there because the WORDED pill's 86px could not share 193px with
              a date range — and every row was three lines, the first of them
              a pill and nothing else: "squashed and stretched". An 18px glyph
              fits beside any range this rail draws; the tone and the title say
              the status, and the word is still there for a screen reader. */}
          <div className="rng">
            <span className="rng-dates">{shortDate(b.checkIn)} – {shortDate(b.checkOut)}</span>
            <StatusPill status={b.status} className="sm" glyph />
          </div>
          <div className="sb">
            <span className="mono">€{b.total}</span>
            <span className="nts">{nightsLabel(nightsBetween(b.checkIn, b.checkOut))}</span>
          </div>
        </div>
        {onOpen && <span className="pencil">✎</span>}
      </div>
    </>
  );
  const cls = 'node' + (on ? ' on' : '') + (isClosedStatus(b.status) ? ' cancel' : '');
  return onOpen ? (
    <button type="button" className={cls} onClick={() => onOpen(b.id)}>
      {inner}
    </button>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

/**
 * A viewing's node on the rail: THE DATE, THE STATUS AND THE TAG, and nothing
 * else (Sir, 2026-09-02). The first cut also named the dogs and the slot, which
 * read as a second stay rather than a note in the history; the household's
 * name is the rail's own header and the dogs are on the booking beneath.
 *
 * A BUTTON only where something can open it; every rail can now, since a
 * viewing opens as a pane of the owner↔booking modal on any page that mounts
 * it.
 */
export function ViewingNode({ v, on = false, onOpen }: { v: Viewing; on?: boolean; onOpen?: (v: Viewing) => void }) {
  const inner = (
    <>
      <div className="spine"><div className="dot" /></div>
      <div className="lab">
        <div className="txt">
          <div className="rng">
            <span className="rng-dates">{shortDate(v.viewingOn)}</span>
            <StatusPill record={VIEWING_STATUS_RECORDS[v.status]} className="sm" glyph />
          </div>
          <div className="sb">
            <span className="bflag-new tint-viewing">Viewing</span>
          </div>
        </div>
        {onOpen && <span className="pencil">✎</span>}
      </div>
    </>
  );
  const cls = 'node viewing' + (on ? ' on' : '');
  return onOpen ? (
    <button type="button" className={cls} onClick={() => onOpen(v)} title="Open this viewing">
      {inner}
    </button>
  ) : (
    <div className={cls} title={on ? 'This viewing' : 'The viewing this household came from'}>
      {inner}
    </div>
  );
}
